import React, { useState } from "react";
import { Button, Col, Form, Row, Table } from "react-bootstrap";
import Select from "react-select";
import { SubmitHandler, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import { ISiteSettingsForm } from "../../types/forms";
import { alertActions } from "../../features/alert/alertSlice";
import { updateSettings } from "../../lib/api";
import { selectAllSettings } from "../../features/global/globalSlice";

type UmpirePay = {
    ageLevel: string;
    umpires: {
        title?: string;
        pay?: number;
    }[];
};

const UmpirePaySettings = (): React.JSX.Element | undefined => {
    const dispatch = useAppDispatch();
    const settings = useAppSelector(selectAllSettings);

    const settingsToShow = [
        "majorybl_umpire_pay",
        "majorybl_max_number_of_umpires",
    ];

    const { setValue, getValues, watch, handleSubmit, formState, reset } =
        useForm<Partial<ISiteSettingsForm>>({
            mode: "onChange",
            defaultValues: {
                umpirepay: settingsToShow.reduce(
                    (acc, settingName) => {
                        const setting = settings.find(
                            s => s.settingName === settingName,
                        );
                        if (setting) {
                            acc[settingName] = setting.setting;
                        }
                        return acc;
                    },
                    {} as Record<string, any>,
                ),
            },
        });

    const maxUmpires = getValues("umpirepay.majorybl_max_number_of_umpires");

    const initialUmpirePay: UmpirePay[] = getValues(
        "umpirepay.majorybl_umpire_pay",
    );

    const umpirePay: UmpirePay[] = watch(
        `umpirepay.majorybl_umpire_pay`,
        initialUmpirePay,
    );

    const [umpireCounts, setUmpireCounts] = useState<Record<string, number>>(
        initialUmpirePay?.reduce(
            (acc: Record<string, number>, s: UmpirePay) => {
                acc[s.ageLevel] = s.umpires.length;
                return acc;
            },
            {},
        ),
    );

    const handleChange = (ageLevel: string, count: number) => {
        const prevCount = umpireCounts[ageLevel];
        const umpire = umpirePay.find(u => u.ageLevel === ageLevel);

        if (count > prevCount) {
            Array.from({ length: count - prevCount }).map((_, i) => {
                umpire?.umpires.push({
                    pay: undefined,
                    title: "",
                });
            });
        } else if (count < prevCount) {
            Array.from({ length: prevCount - count }).map((_, i) => {
                delete umpire?.umpires[i + 1];
            });
        }

        setValue(
            "umpirepay.majorybl_umpire_pay",
            umpirePay.map((u: UmpirePay) =>
                u.ageLevel === ageLevel ? umpire : u,
            ),
            {
                shouldDirty: true,
            },
        );

        setUmpireCounts(prevCounts => {
            return {
                ...prevCounts,
                [ageLevel]: count,
            };
        });
    };

    const handleUmpireChange = (
        ageLevel: string,
        umpireNumber: number,
        label?: string,
        pay?: number,
    ) => {
        const updatedValue = umpirePay.find(u => u.ageLevel === ageLevel);

        updatedValue!.umpires[umpireNumber].title =
            label || updatedValue!.umpires[umpireNumber].title;

        updatedValue!.umpires[umpireNumber].pay =
            pay || updatedValue!.umpires[umpireNumber].pay;

        setValue(
            "umpirepay.majorybl_umpire_pay",
            umpirePay.map((u: UmpirePay) =>
                u.ageLevel === ageLevel ? updatedValue : u,
            ),
            {
                shouldDirty: true,
            },
        );
    };

    const onSubmit: SubmitHandler<Partial<ISiteSettingsForm>> = async data => {
        const dirtyFields = formState.dirtyFields;

        const filteredData: Partial<ISiteSettingsForm> = {
            umpirepay: {},
        };

        (Object.keys(dirtyFields) as Array<keyof ISiteSettingsForm>).forEach(
            category => {
                const dirtySubfields = dirtyFields[category];
                Object.keys(dirtySubfields).forEach(subfield => {
                    if (data[category]?.hasOwnProperty(subfield)) {
                        filteredData[category][subfield] =
                            data[category][subfield];
                    }
                });
            },
        );

        (Object.keys(filteredData) as Array<keyof ISiteSettingsForm>).forEach(
            category => {
                if (Object.keys(filteredData[category]).length === 0) {
                    delete filteredData[category];
                }
            },
        );

        await updateSettings(filteredData)
            .then(() => {
                dispatch(
                    alertActions.success({
                        title: "Settings updated successfully",
                        dismissible: true,
                    }),
                );

                reset(data);
            })
            .catch(e =>
                dispatch(
                    alertActions.error({
                        title: "The server encountered a problem updating the settings.",
                        text: "If the problem continues, please reach out to an admin.",
                        dismissible: true,
                    }),
                ),
            );
    };

    return (
        <section className="umpirePayContent">
            <Form onSubmit={handleSubmit(onSubmit)}>
                <Row>
                    <Col>
                        <Table className="umpirePayTable">
                            <thead>
                                <tr>
                                    <th>Number of Umps</th>
                                    {Array.from({ length: maxUmpires }).map(
                                        (_, i) => (
                                            <React.Fragment key={i}>
                                                <th>{`Umpire ${i + 1} Label`}</th>
                                                <th>{`Umpire ${i + 1} Pay`}</th>
                                            </React.Fragment>
                                        ),
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {umpirePay?.map((p: UmpirePay, idx: number) => (
                                    <tr key={idx}>
                                        <td className="numberOfUmpires">
                                            <Form.Group
                                                controlId={`${p.ageLevel}.number`}
                                            >
                                                <Form.Label>
                                                    {`${p.ageLevel} Number of Umps`}
                                                </Form.Label>
                                                <Select
                                                    defaultValue={{
                                                        value: p.umpires.length,
                                                        label: p.umpires.length,
                                                    }}
                                                    options={Array.from({
                                                        length: maxUmpires,
                                                    }).map((_, i) => ({
                                                        value: i + 1,
                                                        label: i + 1,
                                                    }))}
                                                    isClearable={false}
                                                    isSearchable={false}
                                                    onChange={count =>
                                                        handleChange(
                                                            p.ageLevel,
                                                            count!.value,
                                                        )
                                                    }
                                                    name={`${p.ageLevel}.number`}
                                                />
                                            </Form.Group>
                                        </td>
                                        {Array.from({
                                            length: umpireCounts[p.ageLevel],
                                        }).map((_, i) => {
                                            const umpireNumber = i + 1;
                                            return (
                                                <React.Fragment key={i}>
                                                    <td>
                                                        <Form.Group
                                                            controlId={`${p.ageLevel}.label.${umpireNumber}`}
                                                        >
                                                            <Form.Label>
                                                                {`${p.ageLevel} Umpire ${umpireNumber} Label`}
                                                            </Form.Label>
                                                            <Form.Control
                                                                defaultValue={
                                                                    p.umpires[i]
                                                                        ?.title ||
                                                                    ""
                                                                }
                                                                name={`${p.ageLevel}.label.${umpireNumber}`}
                                                                onBlur={e => {
                                                                    handleUmpireChange(
                                                                        p.ageLevel,
                                                                        i,
                                                                        e.target.value.toString(),
                                                                    );
                                                                }}
                                                            ></Form.Control>
                                                        </Form.Group>
                                                    </td>
                                                    <td>
                                                        <Form.Group
                                                            controlId={`${p.ageLevel}.pay.${umpireNumber}`}
                                                        >
                                                            <Form.Label>
                                                                {`${p.ageLevel} Umpire ${umpireNumber} Pay`}
                                                            </Form.Label>
                                                            <Form.Control
                                                                defaultValue={
                                                                    p.umpires[i]
                                                                        ?.pay ||
                                                                    undefined
                                                                }
                                                                name={`${p.ageLevel}.pay.${umpireNumber}`}
                                                                onBlur={e => {
                                                                    handleUmpireChange(
                                                                        p.ageLevel,
                                                                        i,
                                                                        undefined,
                                                                        parseInt(
                                                                            e.target.value.toString(),
                                                                        ),
                                                                    );
                                                                }}
                                                            ></Form.Control>
                                                        </Form.Group>
                                                    </td>
                                                </React.Fragment>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Col>
                </Row>
                <Button variant="primary" type="submit">
                    Update Settings
                </Button>
            </Form>
        </section>
    );
};

export default UmpirePaySettings;
